'use client'

import * as React from 'react'
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'

import { cn } from '@/lib/utils'

const TooltipProviderScopeContext = React.createContext(false)

function TooltipProvider({
  delay,
  delayDuration,
  ...props
}: TooltipPrimitive.Provider.Props & { delayDuration?: number }) {
  return (
    <TooltipProviderScopeContext.Provider value={true}>
      <TooltipPrimitive.Provider
        data-slot="tooltip-provider"
        delay={delay ?? delayDuration ?? 0}
        {...props}
      />
    </TooltipProviderScopeContext.Provider>
  )
}

function Tooltip({
  delay,
  delayDuration,
  ...props
}: TooltipPrimitive.Root.Props & {
  delay?: number
  delayDuration?: number
}) {
  const hasParentProvider = React.useContext(TooltipProviderScopeContext)

  if (hasParentProvider) {
    return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
  }

  return (
    <TooltipProvider delay={delay} delayDuration={delayDuration}>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  )
}

function TooltipTrigger({
  asChild,
  children,
  ...props
}: TooltipPrimitive.Trigger.Props & { asChild?: boolean }) {
  // When asChild is true, merge trigger props with the child element
  // This avoids nested buttons by applying trigger props directly to the child
  if (asChild && React.isValidElement(children)) {
    return (
      <TooltipPrimitive.Trigger
        data-slot="tooltip-trigger"
        {...props}
        render={(triggerProps) => {
          const child = children as React.ReactElement<any>
          // Merge trigger props with child props
          const mergedProps = {
            ...child.props,
            ...triggerProps,
            // Merge event handlers to call both
            onMouseEnter: (e: React.MouseEvent) => {
              triggerProps?.onMouseEnter?.(e)
              child.props.onMouseEnter?.(e)
            },
            onMouseLeave: (e: React.MouseEvent) => {
              triggerProps?.onMouseLeave?.(e)
              child.props.onMouseLeave?.(e)
            },
            onFocus: (e: React.FocusEvent) => {
              triggerProps?.onFocus?.(e)
              child.props.onFocus?.(e)
            },
            onBlur: (e: React.FocusEvent) => {
              triggerProps?.onBlur?.(e)
              child.props.onBlur?.(e)
            },
            onPointerEnter: (e: React.PointerEvent) => {
              triggerProps?.onPointerEnter?.(e)
              child.props.onPointerEnter?.(e)
            },
            onPointerLeave: (e: React.PointerEvent) => {
              triggerProps?.onPointerLeave?.(e)
              child.props.onPointerLeave?.(e)
            },
            onClick: (e: React.MouseEvent) => {
              triggerProps?.onClick?.(e)
              child.props.onClick?.(e)
            },
            onKeyDown: (e: React.KeyboardEvent) => {
              triggerProps?.onKeyDown?.(e)
              child.props.onKeyDown?.(e)
            },
            // Merge refs
            ref: (node: HTMLElement | null) => {
              if (typeof child.props.ref === 'function') {
                child.props.ref(node)
              } else if (child.props.ref) {
                ;(child.props.ref as React.MutableRefObject<any>).current = node
              }
              if (typeof triggerProps?.ref === 'function') {
                triggerProps.ref(node)
              } else if (triggerProps?.ref) {
                ;(triggerProps.ref as React.MutableRefObject<any>).current =
                  node
              }
            },
          }
          return React.cloneElement(child, mergedProps)
        }}
      />
    )
  }

  return (
    <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props}>
      {children}
    </TooltipPrimitive.Trigger>
  )
}

function TooltipContent({
  className,
  positionerClassName,
  side = 'top',
  sideOffset = 4,
  align = 'center',
  alignOffset = 0,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset'
  > & {
    /** Raise above very high z-index layers (e.g. Sonner at ~1e9). */
    positionerClassName?: string
  }) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className={cn('isolate z-130', positionerClassName)}
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=inline-end]:slide-in-from-left-2 bg-foreground text-background z-130 w-fit max-w-xs origin-(--transform-origin) rounded-md px-3 py-1.5 text-xs',
            className,
          )}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-130 size-2.5 translate-y-[calc(-50%-2px)] rotate-45 rounded-[2px] data-[side=bottom]:top-1 data-[side=inline-end]:top-1/2! data-[side=inline-end]:-left-1 data-[side=inline-end]:-translate-y-1/2 data-[side=inline-start]:top-1/2! data-[side=inline-start]:-right-1 data-[side=inline-start]:-translate-y-1/2 data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5" />
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

function BetterTooltip({
  content,
  align = 'center',
  children,
}: {
  content: React.ReactNode
  align?: 'start' | 'center' | 'end'
  children: React.ReactNode
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        align={align}
        className="font-outfit text-xs font-medium tracking-wider"
      >
        {content}
      </TooltipContent>
    </Tooltip>
  )
}

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  BetterTooltip,
}
